import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import { CAT_YELLOW, CAT_BLACK, CAT_BORDER, CAT_TEXT } from '../utils/styles';
import { BulletItem } from '../utils/BulletItem';
import { PocBox } from '../utils/PocBox';
import { DividerWithLabel } from '../utils/DividerWithLabel';

const CAT_TENANT_ID = 'ceb177bf-013b-49ab-8a9c-4abce32afc1e';

type GrantTypeMode = 'service' | 'user';

const buildClientCredentialsCurl = (clientId: string, secret: string, scope: string) => {
  const resolvedScope = scope
    ? (scope.endsWith('/.default') ? scope : `${scope}/.default`)
    : 'YOUR_SCOPE/.default';

  return `curl --request POST \\\n  --url https://login.microsoftonline.com/${CAT_TENANT_ID}/oauth2/v2.0/token \\\n  --header 'content-type: application/x-www-form-urlencoded' \\\n  --data grant_type=client_credentials \\\n  --data scope=${resolvedScope} \\\n  --data client_id=${clientId || 'YOUR_CLIENT_ID'} \\\n  --data 'client_secret=${secret || 'YOUR_CLIENT_SECRET'}'`;
};

export default function StepTokenGeneration({ classes }: { classes: any }) {
  const { fetch: apiFetch } = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [grantTypeMode, setGrantTypeMode] = useState<GrantTypeMode>('service');
  const [curlCopied, setCurlCopied] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [scope, setScope] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenResponse, setTokenResponse] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [responseCopied, setResponseCopied] = useState(false);
  const [showSample, setShowSample] = useState(false);

  const isServiceFlow = grantTypeMode === 'service';

  const handleCopyCurl = () => {
    const curlCommand = buildClientCredentialsCurl(clientId, clientSecret, scope);
    navigator.clipboard.writeText(curlCommand);
    setCurlCopied(true);
    setTimeout(() => setCurlCopied(false), 2000);
  };

  const handleCopyResponse = () => {
    if (tokenResponse) {
      navigator.clipboard.writeText(tokenResponse);
      setResponseCopied(true);
      setTimeout(() => setResponseCopied(false), 2000);
    }
  };

  const handleGenerateToken = async () => {
    if (!clientId.trim() || !clientSecret.trim() || !scope.trim()) return;

    setLoading(true);
    setTokenResponse(null);
    setTokenError(null);

    try {
      const res = await apiFetch(
        `${backendUrl}/api/token-proxy/${CAT_TENANT_ID}/oauth2/v2.0/token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'client_credentials',
            client_id: clientId.trim(),
            client_secret: clientSecret.trim(),
            scope: scope.trim().endsWith('/.default') ? scope.trim() : `${scope.trim()}/.default`,
          }).toString(),
        },
      );
      const text = await res.text();
      let data: any;
      try { data = JSON.parse(text); } catch {
        throw new Error(`Proxy returned HTTP ${res.status} and a non-JSON body. Is the backend running with the latest config?\n\nPreview: ${text.slice(0, 300)}`);
      }
      setTokenResponse(JSON.stringify(data, null, 2));
    } catch (err: any) {
      const msg: string = err?.message ?? '';
      setTokenError(`Error: ${msg || 'Unknown error. Check the browser console for details.'}`);
    } finally {
      setLoading(false);
    }
  };

  const canGenerate = !!(clientId.trim() && clientSecret.trim() && scope.trim() && !loading);

  return (
    <>
      <Typography variant="body2" style={{ color: '#757575', marginBottom: 20 }}>
        After your Client ID is created, you will receive an IAM email with credentials. Use the tools below to generate and verify an OAuth token.
      </Typography>

      <Accordion className={classes.subAccordion} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>IAM Email - What to expect</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ flexDirection: 'column' }}>
          <BulletItem text="Sender: 'Federation Services Support <Federation_Services_Support@cat.com>'" classes={classes} />
          <BulletItem text="Subject: 'SCTASK## : New Azure Entra ID OAuth Client for <YOUR_CLIENT_NAME>'" classes={classes} />          <BulletItem text="Contains: Client ID, Client Secret, and Token Endpoint URL." classes={classes} />
          <BulletItem text="Credentials are environment-specific (QA or PROD)." classes={classes} />
          <div style={{ marginTop: 12 }}>
            <button
              onClick={() => setShowSample(s => !s)}
              style={{
                background: 'none',
                border: `1px solid ${CAT_BORDER}`,
                borderRadius: 6,
                padding: '5px 12px',
                fontSize: 12,
                color: '#555',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 13 }}>{showSample ? '^' : 'v'}</span>
              {showSample ? 'Hide sample email' : 'View sample email'}
            </button>
            {showSample && (
              <div style={{ marginTop: 10 }}>
                <img
                  src="/screenshots/clientIDEmail.png"
                  alt="IAM credential email"
                  style={{ maxWidth: '100%', borderRadius: 6, border: `1px solid ${CAT_BORDER}`, display: 'block' }}
                />
              </div>
            )}
          </div>
        </AccordionDetails>
      </Accordion>

      <DividerWithLabel label="Generate a Token" classes={classes} />

      <Accordion className={classes.subAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>Manual - cURL / Bruno</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ flexDirection: 'column' }}>
          <Typography style={{ fontSize: 13, color: '#757575', marginBottom: 14, lineHeight: 1.6 }}>
            {isServiceFlow
              ? (
                <>Copy the command below and run it in your terminal. Fill in your <strong>client_id</strong>, <strong>client_secret</strong>, and <strong>scope</strong> from the IAM email.</>
              )
              : (
                <>Configure the <strong>Authorization</strong> tab in <strong>Bruno</strong> using the values below. The tool handles the browser redirect and token exchange automatically.</>
              )}
          </Typography>

          {isServiceFlow ? (
            <div style={{ position: 'relative', background: '#1A1A1A', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid #333' }}>
                <Typography style={{ fontSize: 11, color: '#888', fontWeight: 600, letterSpacing: 0.8 }}>CURL</Typography>
                <Tooltip title={curlCopied ? 'Copied!' : 'Copy'}>
                  <IconButton size="small" onClick={handleCopyCurl} style={{ padding: 4 }}>
                    <FileCopyIcon style={{ fontSize: 13, color: '#AAAAAA' }} />
                  </IconButton>
                </Tooltip>
              </div>
              <pre style={{ margin: 0, padding: '12px 16px', fontSize: 12, color: '#E8E8E8', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.8 }}>
                {buildClientCredentialsCurl('', '', '')}
              </pre>
            </div>
          ) : (
            <div style={{ border: `1px solid ${CAT_BORDER}`, borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ background: '#F5F5F5', padding: '8px 14px', borderBottom: `1px solid ${CAT_BORDER}` }}>
                <Typography style={{ fontSize: 11, color: '#888', fontWeight: 600, letterSpacing: 0.8 }}>BRUNO — Authorization Tab</Typography>
              </div>
              <div style={{ padding: '12px 16px' }}>
                {[
                  { label: 'Grant Type', value: 'Authorization Code' },
                  { label: 'Callback URL', value: 'https://oauth.pstmn.io/v1/callback  —  Postman\'s built-in callback. Postman intercepts this redirect internally; no server needed. For Bruno, use http://localhost.' },
                  { label: 'Authorization URL', value: `https://login.microsoftonline.com/${CAT_TENANT_ID}/oauth2/v2.0/authorize` },
                  { label: 'Access Token URL', value: `https://login.microsoftonline.com/${CAT_TENANT_ID}/oauth2/v2.0/token` },
                  { label: 'Client ID', value: 'Your Client ID (from IAM email)' },
                  { label: 'Client Secret', value: 'Your Client Secret (from IAM email)' },
                  { label: 'Scope', value: 'Your API scope, e.g. 3e93123b-b8f8-4c2e-aa9c-24338ac0764d/.default' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'flex-start' }}>
                    <Typography style={{ fontSize: 12.5, fontWeight: 700, color: CAT_TEXT, minWidth: 160, flexShrink: 0 }}>{label}</Typography>
                    <Typography style={{ fontSize: 12.5, color: '#555', wordBreak: 'break-all' }}>{value}</Typography>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Typography style={{ fontSize: 13, fontWeight: 700, color: CAT_TEXT, marginBottom: 10 }}>
            {isServiceFlow ? 'Or use Bruno' : 'Steps in Bruno'}
          </Typography>
          {(isServiceFlow
            ? [
                'Create a new POST request.',
                `URL: https://login.microsoftonline.com/${CAT_TENANT_ID}/oauth2/v2.0/token`,
                'Body -> Form-URL-Encoded.',
                'Fields: grant_type=client_credentials, client_id, client_secret, scope (append /.default to scope).',
                'Send and copy the access_token from the response.',
              ]
            : [
                'Open your request in Bruno and go to the Authorization tab.',
                'Set Grant Type to Authorization Code.',
                'Fill in the Authorization URL, Access Token URL, Client ID, Client Secret, and Scope from the config above.',
                'Click Get New Access Token — a browser window will open for you to sign in.',
                'After signing in, copy the access_token from the response.',
              ]).map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 7, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 20, height: 20, borderRadius: '50%', background: CAT_YELLOW, color: CAT_BLACK, fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
              <Typography style={{ fontSize: 13, color: CAT_TEXT, lineHeight: 1.6 }}>{t}</Typography>
            </div>
          ))}
        </AccordionDetails>
      </Accordion>

      <Accordion className={classes.subAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.subAccordionSummary}>Try it here - In-page Token Generator</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.subAccordionDetails} style={{ flexDirection: 'column' }}>

          {/* Grant type selector — always visible so users can switch */}
          <div
            style={{
              border: `1px solid ${CAT_BORDER}`,
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 16,
              background: '#FAFAFA',
            }}
          >
            <Typography style={{ fontSize: 12.5, fontWeight: 700, color: CAT_TEXT, marginBottom: 6 }}>
              Select grant type
            </Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={grantTypeMode === 'service'}
                    onChange={() => {
                      setGrantTypeMode('service');
                      setTokenError(null);
                      setTokenResponse(null);
                    }}
                    style={{ color: CAT_YELLOW }}
                  />
                }
                label={<span style={{ fontSize: 13 }}>Service based / Pseudo User (client_credentials)</span>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={grantTypeMode === 'user'}
                    onChange={() => {
                      setGrantTypeMode('user');
                      setTokenError(null);
                      setTokenResponse(null);
                    }}
                    style={{ color: CAT_YELLOW }}
                  />
                }
                label={<span style={{ fontSize: 13 }}>User based (authorization_code)</span>}
              />
            </div>
          </div>

          {!isServiceFlow ? (
            <div style={{ background: '#FFF8E1', border: `1px solid #FFE082`, borderRadius: 8, padding: '14px 18px' }}>
              <Typography style={{ fontSize: 13, fontWeight: 700, color: '#5D4037', marginBottom: 6 }}>
                In-page generation is not supported for User-based (authorization_code) flow
              </Typography>
              <Typography style={{ fontSize: 13, color: '#5D4037', lineHeight: 1.7 }}>
                OAuth2 authorization_code flow requires a <strong>redirect URI pre-registered in Azure</strong> for your specific app.
                Because every team's app registration is different, we cannot guarantee a matching redirect URI here.
              </Typography>
              <Typography style={{ fontSize: 13, color: '#5D4037', lineHeight: 1.7, marginTop: 8 }}>
                Use  <strong>Bruno</strong> instead — their built-in OAuth2 flow uses{' '}
                <code style={{ fontSize: 12, background: '#FFF3CD', padding: '1px 4px', borderRadius: 3 }}>https://oauth.pstmn.io/v1/callback</code>{' '}
                which Bruno manages on your behalf. See the <em>Manual</em> section above for exact configuration.
              </Typography>
            </div>
          ) : (
            <>
          <Typography style={{ fontSize: 12.5, color: '#9E9E9E', marginBottom: 16, lineHeight: 1.6 }}>
            {'Credentials are sent to the Service Catalog backend token proxy, which forwards them to Microsoft\'s token endpoint. Nothing is stored or cached.'}
          </Typography>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
            <div>
              <Typography style={{ fontSize: 12, fontWeight: 700, color: CAT_TEXT, marginBottom: 5 }}>Client ID</Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                placeholder="e.g. 1234abcd-12ab-34cd-56ef-1234567890ab"
                InputProps={{ style: { fontSize: 13.5, borderRadius: 8 } }}
              />
            </div>
            <div>
              <Typography style={{ fontSize: 12, fontWeight: 700, color: CAT_TEXT, marginBottom: 5 }}>Client Secret</Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="password"
                value={clientSecret}
                onChange={e => setClientSecret(e.target.value)}
                placeholder="Secret from Federation email"
                InputProps={{ style: { fontSize: 13.5, borderRadius: 8 } }}
              />
            </div>
            <div>
              <Typography style={{ fontSize: 12, fontWeight: 700, color: CAT_TEXT, marginBottom: 5 }}>
                Scope <span style={{ fontWeight: 400, color: '#9E9E9E' }}>(/.default appended automatically)</span>
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                value={scope}
                onChange={e => setScope(e.target.value)}
                placeholder="Your API scope, e.g. 1234abcd-12ab-34cd-56ef-1234567890ab/.default"
                InputProps={{ style: { fontSize: 13.5, borderRadius: 8 } }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
            <Button
              variant="contained"
              disabled={!canGenerate}
              onClick={handleGenerateToken}
              style={{
                background: canGenerate ? CAT_YELLOW : '#E0E0E0',
                color: canGenerate ? CAT_BLACK : '#999',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 8,
                padding: '8px 24px',
                fontSize: 13.5,
                boxShadow: 'none',
                minWidth: 160,
              }}
            >
              {loading ? <CircularProgress size={16} style={{ color: CAT_BLACK }} /> : 'Generate Token'}
            </Button>
            <Tooltip title={curlCopied ? 'Copied!' : 'Copy filled values as cURL'}>
              <Button
                variant="outlined"
                onClick={handleCopyCurl}
                style={{ textTransform: 'none', borderRadius: 8, fontSize: 13, borderColor: CAT_BORDER, color: CAT_TEXT, padding: '7px 16px' }}
              >
                <FileCopyIcon style={{ fontSize: 14, marginRight: 6 }} />
                {curlCopied ? 'Copied!' : 'Copy as cURL'}
              </Button>
            </Tooltip>
          </div>

          {tokenResponse && (
            <div style={{ background: '#1A1A1A', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid #333' }}>
                <Typography style={{ fontSize: 11.5, color: '#AAAAAA', fontWeight: 600, letterSpacing: 0.5 }}>RESPONSE</Typography>
                <Tooltip title={responseCopied ? 'Copied!' : 'Copy response'}>
                  <IconButton size="small" onClick={handleCopyResponse} style={{ padding: 4 }}>
                    <FileCopyIcon style={{ fontSize: 13, color: '#AAAAAA' }} />
                  </IconButton>
                </Tooltip>
              </div>
              <pre style={{ margin: 0, padding: '12px 16px', fontSize: 12.5, color: '#E8E8E8', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.7 }}>
                {tokenResponse}
              </pre>
            </div>
          )}

          {tokenError && (
            <div style={{ background: '#FFF3F3', border: '1px solid #F5C6CB', borderRadius: 8, padding: '12px 16px' }}>
              <Typography style={{ fontSize: 13, color: '#B00020', lineHeight: 1.6 }}>{tokenError}</Typography>
            </div>
          )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <PocBox
        classes={classes}
        contact="Federation Services Support Team"
        email="Federation_Services_Support@cat.com"
        issues={['Token request returning 401', 'Expired credentials', 'Wrong grant_type', 'Token not accepted by downstream API']}
      />
    </>
  );
}
